import { useEffect, useState } from 'react';
import { createStore as createIdbStore, del, get, keys, set } from 'idb-keyval';
import type { UseStore } from 'idb-keyval';
import type { MatchSnapshot } from '@bgf/protocol';
import type { MatchStore } from '@bgf/client';
import { useProfile } from './ProfileProvider';
import type { GameId } from '../games/ids';
import { GAME_IDS } from '../games/ids';

/** What changed, when known (stores created through `getMatchStore` know their owner). */
export interface MatchStoreEvent {
  slug?: string;
  game?: GameId;
  id?: string;
  kind: 'put' | 'delete';
}
type Listener = (event: MatchStoreEvent) => void;
const listeners = new Set<Listener>();

/** Fires whenever any IdbMatchStore writes, so lists can refresh (and devices can sync). */
export const matchStoreBus = {
  subscribe(l: Listener) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
  emit(event: MatchStoreEvent = { kind: 'put' }) {
    for (const l of Array.from(listeners)) l(event);
  },
};

/** The little every saved snapshot must have, whatever game it belongs to. */
export interface StoredSnapshot {
  id: string;
  code: string;
  seq: number;
  updatedAt: number;
}

function isSnapshot(v: unknown): v is StoredSnapshot {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as StoredSnapshot).id === 'string' &&
    typeof (v as StoredSnapshot).code === 'string' &&
    typeof (v as StoredSnapshot).seq === 'number'
  );
}

export { matchDbName, MATCH_STORE_NAME } from './matchDb';
import { MATCH_STORE_NAME, matchDbName } from './matchDb';
/** Database name used before games became a route segment (backgammon only). */
export function legacyMatchDbName(slug: string): string {
  return `bgf-matches-${slug}`;
}

/**
 * One player's saved snapshots of one game in IndexedDB. Game-agnostic: backgammon stores
 * `MatchSnapshot`s, table games store `TableSnapshot`s.
 */
export class IdbSnapshotStore<T extends StoredSnapshot> {
  private readonly store: UseStore;

  constructor(
    dbName: string,
    storeName: string = MATCH_STORE_NAME,
    private readonly owner: { slug: string; game: GameId } | null = null,
  ) {
    this.store = createIdbStore(dbName, storeName);
  }

  async list(): Promise<T[]> {
    const ks = await keys(this.store);
    const out: T[] = [];
    for (const k of ks) {
      const v = await get(k, this.store);
      if (isSnapshot(v)) out.push(v as T);
    }
    return out.sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async get(id: string): Promise<T | undefined> {
    const v = await get(id, this.store);
    return isSnapshot(v) ? (v as T) : undefined;
  }

  async put(snapshot: T): Promise<void> {
    await set(snapshot.id, snapshot, this.store);
    matchStoreBus.emit({ ...this.owner, id: snapshot.id, kind: 'put' });
  }

  async delete(id: string): Promise<void> {
    await del(id, this.store);
    matchStoreBus.emit({ ...this.owner, id, kind: 'delete' });
  }

  /** Find a saved snapshot by its room code (most recently updated wins). */
  async findByCode(code: string): Promise<T | undefined> {
    const all = await this.list();
    return all.find((m) => m.code === code);
  }
}

export class IdbMatchStore extends IdbSnapshotStore<MatchSnapshot> implements MatchStore {}

const shared = new Map<string, IdbMatchStore>();

/** The saved-match store of one player for one game (memoised). */
export function getMatchStore(slug: string, game: GameId): IdbMatchStore {
  const key = `${slug}/${game}`;
  let s = shared.get(key);
  if (!s) {
    s = new IdbMatchStore(matchDbName(slug, game), MATCH_STORE_NAME, { slug, game });
    shared.set(key, s);
  }
  return s;
}

/**
 * The same store typed for another snapshot shape (table games). Backgammon and table games
 * never share a game id, so a database only ever holds one shape.
 */
export function getSnapshotStore<T extends StoredSnapshot>(
  slug: string,
  game: GameId,
): IdbSnapshotStore<T> {
  return getMatchStore(slug, game) as unknown as IdbSnapshotStore<T>;
}

/** For tests: replace the store of a slug+game (or clear all with no arguments). */
export function setMatchStoreForTests(slug?: string, game?: GameId, store?: IdbMatchStore): void {
  if (slug === undefined || game === undefined) shared.clear();
  else if (store) shared.set(`${slug}/${game}`, store);
  else shared.delete(`${slug}/${game}`);
}

/**
 * Copy every snapshot from a legacy database into `target` (skipping ids already present with
 * an equal or newer `seq`). Best effort: errors are swallowed.
 */
export async function copyLegacyMatches(
  legacyDbName: string,
  target: IdbMatchStore,
  legacyStoreName = 'bgf-matches',
): Promise<number> {
  let copied = 0;
  try {
    const legacy = createIdbStore(legacyDbName, legacyStoreName);
    for (const k of await keys(legacy)) {
      const v = await get(k, legacy);
      if (!isSnapshot(v)) continue;
      const existing = await target.get(v.id);
      if (existing && existing.seq >= v.seq) continue;
      await target.put(v as MatchSnapshot);
      copied++;
    }
  } catch {
    /* legacy db unreadable: nothing to migrate */
  }
  return copied;
}

export interface SavedSnapshotsState<T> {
  matches: T[];
  loading: boolean;
  refresh: () => void;
  remove: (id: string) => Promise<void>;
}
export type SavedMatchesState = SavedSnapshotsState<MatchSnapshot>;

/** Saved snapshots of the profile in scope for one game, typed for that game. */
export function useSavedSnapshots<T extends StoredSnapshot>(game: GameId): SavedSnapshotsState<T> {
  const { slug } = useProfile();
  const key = `${slug}/${game}`;
  const [data, setData] = useState<{ key: string; matches: T[] } | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getSnapshotStore<T>(slug, game)
      .list()
      .then((list) => {
        if (!cancelled) setData({ key, matches: list });
      })
      .catch(() => {
        if (!cancelled) setData({ key, matches: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [tick, slug, game, key]);

  useEffect(() => matchStoreBus.subscribe(() => setTick((t) => t + 1)), []);

  const loading = data?.key !== key;
  return {
    matches: loading ? [] : data!.matches,
    loading,
    refresh: () => setTick((t) => t + 1),
    remove: async (id: string) => {
      await getMatchStore(slug, game).delete(id);
    },
  };
}

/** Saved matches of the profile in scope for one game. */
export function useSavedMatches(game: GameId): SavedMatchesState {
  return useSavedSnapshots<MatchSnapshot>(game);
}

export interface SavedMatchesByGame {
  game: GameId;
  matches: MatchSnapshot[];
}

/** Saved matches of the profile in scope across every game (for the games hub). */
export function useAllSavedMatches(): { byGame: SavedMatchesByGame[]; loading: boolean } {
  const { slug } = useProfile();
  const [data, setData] = useState<{ slug: string; byGame: SavedMatchesByGame[] } | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      GAME_IDS.map(async (game) => ({
        game,
        matches: await getMatchStore(slug, game)
          .list()
          .catch(() => [] as MatchSnapshot[]),
      })),
    ).then((byGame) => {
      if (!cancelled) setData({ slug, byGame });
    });
    return () => {
      cancelled = true;
    };
  }, [tick, slug]);

  useEffect(() => matchStoreBus.subscribe(() => setTick((t) => t + 1)), []);

  const loading = data?.slug !== slug;
  return { byGame: loading ? [] : data!.byGame, loading };
}
